import React, { useEffect, useState } from 'react';
import { BadgeGroup } from './ui/badge-group';

/**
 * Toast Notification Component
 * Shows bottom-center notifications with smooth animations
 * Auto-dismisses after specified duration
 * 
 * Types:
 * - success: Green - For successful operations
 * - error: Red - For failed operations
 * - warning: Yellow - For caution messages
 * - brand: Blue - For general info
 * - gray: Gray - For neutral system messages
 */
export const ToastNotification = ({
  type = 'success', // 'success' | 'error' | 'warning' | 'brand' | 'gray'
  message,
  addonText,
  isVisible,
  onClose,
  duration = 2500 // Auto-dismiss after 2.5 seconds
}) => {
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(!isVisible);

  const handleDismiss = React.useCallback(() => {
    // Start fade-out animation
    setIsAnimatingOut(true);

    // Wait for animation to complete (300ms)
    setTimeout(() => {
      setIsAnimatingOut(false);
      setIsAnimatingIn(true);
      if (onClose) {
        onClose();
      }
    }, 300);
  }, [onClose]);

  useEffect(() => {
    if (isVisible) {
      // Trigger slide-in animation after brief moment
      const animationTimeout = setTimeout(() => {
        setIsAnimatingIn(false);
      }, 50);

      // Auto-dismiss after duration
      const dismissTimeout = setTimeout(() => {
        handleDismiss();
      }, duration);

      return () => {
        clearTimeout(animationTimeout);
        clearTimeout(dismissTimeout);
      };
    }
  }, [isVisible, duration, handleDismiss]);

  // Don't render if not visible and not animating
  if (!isVisible && !isAnimatingOut) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={`fixed bottom-8 transition-all ease-in-out ${
        isAnimatingOut || isAnimatingIn
          ? 'opacity-0 translate-y-4'
          : 'opacity-100 translate-y-0'
      }`}
      style={{
        left: 'calc(50vw + 32px)',
        transform: 'translateX(-50%)',
        transition: 'opacity 300ms ease-in-out, transform 300ms ease-in-out',
        zIndex: 9999
      }}
      data-testid="toast-notification"
    >
      <BadgeGroup
        color={type}
        theme="light"
        align="leading"
        size="lg"
        addonText={addonText}
        iconTrailing={null}
      >
        {message}
      </BadgeGroup>
    </div>
  );
};

export default ToastNotification;
